import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

const EMPTY = { code: "", description: "", discount_type: "percent", discount_value: 10, max_uses: 0, expires_at: "", applies_to: "all", is_active: true };

const PromoForm = ({ form, setForm, onSubmit }) => (
  <div className="space-y-3">
    <div><Label className="text-sm">Code</Label><Input className="uppercase" value={form.code} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="SPRING25" data-testid="promo-form-code" /></div>
    <div><Label className="text-sm">Description</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="promo-form-description" /></div>
    <div className="grid grid-cols-2 gap-3">
      <div><Label className="text-sm">Type</Label>
        <Select value={form.discount_type} onValueChange={v => setForm({ ...form, discount_type: v })}>
          <SelectTrigger data-testid="promo-form-type"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="percent">Percentage (%)</SelectItem><SelectItem value="fixed">Fixed amount ($)</SelectItem></SelectContent>
        </Select>
      </div>
      <div><Label className="text-sm">Value</Label><Input type="number" min="0" value={form.discount_value} onChange={e => setForm({ ...form, discount_value: e.target.value })} data-testid="promo-form-value" /></div>
      <div><Label className="text-sm">Max uses (0 = unlimited)</Label><Input type="number" min="0" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })} data-testid="promo-form-max-uses" /></div>
      <div><Label className="text-sm">Expires</Label><Input type="date" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} data-testid="promo-form-expires" /></div>
    </div>
    <button onClick={onSubmit} className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-2.5 rounded-lg" data-testid="promo-form-submit">Create Code</button>
  </div>
);

export const PromoCodesTab = () => {
  const [codes, setCodes] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => api.get("/api/admin/promo-codes").then(r => setCodes(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.code.trim()) { toast.error("Code required"); return; }
    try {
      await api.post("/api/admin/promo-codes", { ...form, discount_value: Number(form.discount_value), max_uses: Number(form.max_uses), expires_at: form.expires_at ? `${form.expires_at}T23:59:59+00:00` : null });
      toast.success("Promo code created"); setForm(EMPTY); setOpen(false); load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(e => e.msg).join(", ") : d || "Failed");
    }
  };
  const toggle = async (c) => {
    const { id, uses_count, created_at, ...rest } = c;
    await api.put(`/api/admin/promo-codes/${id}`, { ...rest, is_active: !c.is_active }); load();
  };
  const remove = async (id) => { await api.delete(`/api/admin/promo-codes/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm overflow-x-auto" data-testid="promo-codes-tab">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-slate-900">Promo Codes</h2>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><button className="bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium px-4 py-2 rounded-lg flex items-center gap-1.5" data-testid="promo-create-btn"><Plus className="w-4 h-4" />New Code</button></DialogTrigger>
          <DialogContent className="bg-white"><DialogHeader><DialogTitle>New Promo Code</DialogTitle></DialogHeader><PromoForm form={form} setForm={setForm} onSubmit={create} /></DialogContent>
        </Dialog>
      </div>
      <Table>
        <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Discount</TableHead><TableHead>Uses</TableHead><TableHead>Expires</TableHead><TableHead>Source</TableHead><TableHead>Active</TableHead><TableHead /></TableRow></TableHeader>
        <TableBody>
          {codes.map(c => (
            <TableRow key={c.id} data-testid={`promo-row-${c.code}`}>
              <TableCell><p className="font-mono font-bold text-slate-900">{c.code}</p><p className="text-xs text-slate-400">{c.description}</p></TableCell>
              <TableCell className="font-medium">{c.discount_type === "percent" ? `${c.discount_value}%` : `$${c.discount_value}`}</TableCell>
              <TableCell className="text-sm">{c.uses_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</TableCell>
              <TableCell className="text-xs text-slate-500">{c.expires_at ? c.expires_at.split("T")[0] : "Never"}</TableCell>
              <TableCell className="text-xs">{c.rep_id ? <span className="bg-purple-50 text-purple-700 px-2 py-0.5 rounded">Referral</span> : <span className="bg-slate-100 px-2 py-0.5 rounded">Admin</span>}</TableCell>
              <TableCell><Switch checked={c.is_active} onCheckedChange={() => toggle(c)} data-testid={`promo-toggle-${c.code}`} /></TableCell>
              <TableCell><button onClick={() => remove(c.id)} className="text-red-500 hover:text-red-700" data-testid={`promo-delete-${c.code}`}><Trash2 className="w-4 h-4" /></button></TableCell>
            </TableRow>
          ))}
          {codes.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-6">No promo codes yet</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
};
